import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../shared/toastr.service";
import { MatDialog } from "@angular/material/dialog";
import {
  GET_IMAGES,
  GetImagesSuccessAction,
  GetImagesFailureAction,
  REMOVE_IMAGE,
  RemoveImageAction,
  RemoveImageSuccessAction,
  RemoveImageFailureAction,
  ADD_IMAGE,
  ADD_IMAGE_FAILURE,
  AddImageAction,
  AddImageSuccessAction,
  AddImageFailureAction,
  CHANGE_IMAGE,
  ChangeImageAction,
  ChangeImageSuccessAction,
  ChangeImageFailureAction,
} from "./images.actions";
import { ImagesService } from "./images.service";
import { AddImageObject, ChangeImageObject, Image } from "./media.interface";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";

@Injectable()
export class ImagesEffects {
  getImages$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GET_IMAGES),
      switchMap(() => {
        return this.imagesService.getImages().pipe(
          switchMap((response: Image[]) => {
            return [new GetImagesSuccessAction(response)];
          }),
          catchError((error) => of(new GetImagesFailureAction(error))),
        );
      }),
    );
  });

  removeImage$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(REMOVE_IMAGE),
      map((action: RemoveImageAction) => action.payload),
      switchMap((payload: string) => {
        return this.imagesService.removeImage(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant("media.images.deleteSuccess") as string,
            );
            return [new RemoveImageSuccessAction(payload)];
          }),
          catchError((error) => of(new RemoveImageFailureAction(error))),
        );
      }),
    ),
  );

  addImage$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_IMAGE),
      map((action: AddImageAction) => action.payload),
      switchMap((payload: AddImageObject) => {
        return this.imagesService.addImage(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("media.images.addSuccess") as string,
            );
            return [new AddImageSuccessAction(response)];
          }),
          catchError((error) => of(new AddImageFailureAction(error))),
        );
      }),
    ),
  );

  addImageFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ADD_IMAGE_FAILURE),
        tap((action: AddImageFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("MEDIA_STORAGE_LIMIT_REACHED")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "mediaStorage" },
            });
          } else {
            this.toastr.error(
              this.translate.instant("media.images.addError") as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  changeImage$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_IMAGE),
      map((action: ChangeImageAction) => action.payload),
      switchMap((payload: ChangeImageObject) => {
        return this.imagesService.changeImage(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant("media.images.editSuccess") as string,
            );
            return [new ChangeImageSuccessAction(payload)];
          }),
          catchError((error) => of(new ChangeImageFailureAction(error))),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private imagesService: ImagesService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}
}
